import { useGetStudentCoinBalanceQuery } from 'src/api/endpoints/student-coins-balance/api';
import { InputNumber, InputNumberChangeEvent } from 'primereact/inputnumber';
import { Formik, FormikErrors, FormikValues, useFormik } from 'formik';
import { FormButtons } from 'src/components/design-system/form/buttons';
import {
    ChangeEvent,
    FC,
    PropsWithChildren,
    ReactElement,
    useCallback,
} from 'react';
import { Form } from 'src/components/design-system/form';
import { InputText } from 'primereact/inputtext';
import classNames from 'classnames';
import { v4 as uuidV4 } from 'uuid';
import './module.scss';
import { DatePicker } from 'antd';
import locale from 'antd/lib/date-picker/locale/ru_RU';
import dayjs, { Dayjs } from 'dayjs';

export type FormRowProps = PropsWithChildren<{
    // Default 16px between elements
    gap?: string;
}>;

export const FormRow: FC<FormRowProps> = (
    props: FormRowProps
): ReactElement => {
    const { children, gap = '16px' } = props;

    return (
        <div className={'form-row'} style={{ gap: gap }}>
            {children}
        </div>
    );
};

export type TextFieldProps<T extends FormikValues> = {
    id?: string;
    label?: string;
    placeholder?: string;
    autoFocus?: boolean;
    description?: string;
    autoComplete?: string;
    /** If not has change event, not showing errors
     @default true
     */
    checkTouched?: boolean;
    // Default text
    type?: 'text' | 'password';
} & ({ name?: string; formik?: never } | { name: keyof T; formik: Formik<T> });

export const TextField = <T extends FormikValues>(
    props: TextFieldProps<T>
): ReactElement => {
    const {
        id = uuidV4(),
        autoFocus = false,
        autoComplete,
        formik,
        name = id,
        checkTouched = true,
        type = 'text',
    } = props;

    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>): void => {
            if (!formik) return;

            const value: string = event.currentTarget.value;
            formik.setFieldValue(name.toString(), value, true).then(() => {
                formik.setFieldTouched(name.toString(), true);
            });
        },
        [formik, name]
    );

    const getValue = useCallback((): string | undefined => {
        if (!formik) return undefined;

        return formik.values[name];
    }, [formik, name]);

    const getIncorrect = useCallback((): boolean => {
        if (!formik) return false;

        if (checkTouched && !formik.getFieldMeta(name.toString()).touched)
            return false;

        return !!formik.errors[name];
    }, [formik, name]);

    return (
        <div className={classNames('form-field text-field')}>
            {props.label && (
                <label
                    className={classNames('field-label', 'text-field-label')}
                    htmlFor={id}
                >
                    {props.label}
                </label>
            )}
            <InputText
                id={id}
                type={type}
                value={getValue()}
                onChange={onChange}
                name={name.toString()}
                autoComplete={autoComplete}
                autoFocus={autoFocus}
                placeholder={props.placeholder}
                className={classNames('field-input', 'text-field-input', {
                    incorrect: getIncorrect(),
                })}
            />
            {formik && getIncorrect() && formik.errors[name] ? (
                <div
                    className={classNames('field-error', 'integer-field-error')}
                >
                    {formik.errors[name] as string}
                </div>
            ) : (
                <div
                    className={classNames(
                        'field-description',
                        'integer-field-description'
                    )}
                >
                    {props.description}
                </div>
            )}
        </div>
    );
};

export type IntegerFieldProps<T extends FormikValues> = {
    id?: string;
    label?: string;
    placeholder?: string;
    autoFocus?: boolean;
    description?: string;
} & ({ name?: string; formik?: never } | { name: keyof T; formik: Formik<T> });

export type DateFieldProps<T extends FormikValues> = {
    id?: string;
    label?: string;
    placeholder?: string;
    autoFocus?: boolean;
    description?: string;
    maxDate?: Dayjs;
    minDate?: Dayjs;
} & ({ name?: string; formik?: never } | { name: keyof T; formik: Formik<T> });

export const DateField = <T extends FormikValues>(
    props: DateFieldProps<T>
): ReactElement => {
    const {
        id = uuidV4(),
        autoFocus = false,
        formik,
        name = id,
        maxDate,
        minDate,
    } = props;

    const onChange = useCallback(
        (date: Dayjs): void => {
            if (!formik) return;

            formik.setFieldValue(name.toString(), date, true);
        },
        [formik, name]
    );

    const getValue = useCallback((): Dayjs | null => {
        if (!formik) return null;

        return formik.values[name];
    }, [formik, name]);

    const getIncorrect = useCallback((): boolean => {
        if (!formik) return false;

        return !!formik.errors[name];
    }, [formik, name]);

    return (
        <div
            className={classNames('form-field date-field', {
                incorrect: getIncorrect(),
            })}
        >
            {props.label && (
                <label
                    className={classNames('field-label', 'date-field-label')}
                    htmlFor={id}
                >
                    {props.label}
                </label>
            )}
            <DatePicker
                locale={{
                    ...locale,
                    lang: {
                        ...locale.lang,
                        fieldDateFormat: 'DD.MM.YYYY',
                        monthFormat: 'MMMM',
                        monthBeforeYear: false,
                    },
                }}
                maxDate={maxDate}
                minDate={minDate}
                showNow={false}
                cellRender={(date, info) => {
                    if (info.type === 'month') {
                        return dayjs(date).format('MMM');
                    }

                    return info.originNode;
                }}
                id={id}
                className={classNames('field-input', 'date-field-input')}
                name={name.toString()}
                autoFocus={autoFocus}
                value={getValue()}
                placeholder={props.placeholder}
                onChange={onChange}
            />
            {formik && formik.errors[name] ? (
                <div className={classNames('field-error', 'date-field-error')}>
                    {formik.errors[name] as string}
                </div>
            ) : (
                <div
                    className={classNames(
                        'field-description',
                        'date-field-description'
                    )}
                >
                    {props.description}
                </div>
            )}
        </div>
    );
};

export const IntegerField = <T extends FormikValues>(
    props: IntegerFieldProps<T>
): ReactElement => {
    const { id = uuidV4(), autoFocus = false, formik, name = id } = props;

    const onChange = useCallback(
        (event: InputNumberChangeEvent): void => {
            // if (props.onInput) return props.onInput(event);
            if (!formik) return;

            const value: number | null = event.value;
            formik.setFieldValue(name.toString(), value, true);
        },
        [formik, name]
    );

    const getValue = useCallback((): number | null => {
        // if (props.value) return props.value;
        if (!formik) return null;

        return formik.values[name];
    }, [formik, name]);

    const getIncorrect = useCallback((): boolean => {
        // if (props.incorrect !== undefined) return props.incorrect;
        if (!formik) return false;

        return !!formik.errors[name];
    }, [formik, name]);

    return (
        <div
            className={classNames('form-field integer-field', {
                incorrect: getIncorrect(),
            })}
        >
            {props.label && (
                <label
                    className={classNames('field-label', 'integer-field-label')}
                    htmlFor={id}
                >
                    {props.label}
                </label>
            )}
            <InputNumber
                name={name.toString()}
                useGrouping={false}
                min={0}
                autoFocus={autoFocus}
                onChange={onChange}
                value={getValue()}
                inputId={id}
                placeholder={props.placeholder}
                className={classNames('field-input', 'integer-field-input')}
            />
            {formik && formik.errors[name] ? (
                <div
                    className={classNames('field-error', 'integer-field-error')}
                >
                    {formik.errors[name] as string}
                </div>
            ) : (
                <div
                    className={classNames(
                        'field-description',
                        'integer-field-description'
                    )}
                >
                    {props.description}
                </div>
            )}
        </div>
    );
};

export type DebitStudentCoinsFormValues = {
    amount: number;
    description: string;
};

export type DebitStudentCoinsFormProps = {
    studentId: number;
    onSubmit?: (values: DebitStudentCoinsFormValues) => Promise<void>;
    onCancel?: () => void;
};

export const DebitStudentCoinsForm: FC<DebitStudentCoinsFormProps> = (
    props
): ReactElement => {
    const { studentId, onSubmit } = props;

    const { data: studentCoinBalance, isLoading: isStudentCoinBalanceLoading } =
        useGetStudentCoinBalanceQuery({ params: { studentId } });

    const formik = useFormik<Partial<DebitStudentCoinsFormValues>>({
        initialValues: {
            amount: undefined,
            description: '',
        },
        validate: (
            values: Partial<DebitStudentCoinsFormValues>
        ): FormikErrors<Partial<DebitStudentCoinsFormValues>> => {
            if (!studentCoinBalance) return {};

            const errors: FormikErrors<Partial<DebitStudentCoinsFormValues>> =
                {};

            const { amount, description } = values;

            if (typeof amount !== 'number') {
                errors.amount = 'Поле должно быть заполнено';
            } else {
                if (amount > studentCoinBalance) {
                    errors.amount =
                        'Сумма списания не может быть больше баланса';
                }

                if (amount === 0) {
                    errors.amount = 'Значение должно быть положительным числом';
                }
            }

            if (!description || !description.trim().length) {
                errors.description = 'Поле должно быть заполнено';
            }

            return errors;
        },
        onSubmit: async values => {
            if (onSubmit) await onSubmit(values as DebitStudentCoinsFormValues);
        },
    });

    if (isStudentCoinBalanceLoading || !studentCoinBalance) {
        return <></>;
    }

    return (
        <Form formik={formik} prohibitAutoComplete={true}>
            <div className={'balance-info'}>
                <p className={'unselectable'}>Текущий баланс:</p>
                <span className={'balance-value'}>
                    {studentCoinBalance.toFixed(2).replace('.', ',')}
                </span>
            </div>

            <FormRow>
                <IntegerField
                    autoFocus={true}
                    label={'Сумма к списанию'}
                    placeholder={'Введите сумму'}
                    formik={formik}
                    name={'amount'}
                    description={'Положительное число'}
                />
            </FormRow>

            <FormRow>
                <TextField
                    label={'Описание'}
                    placeholder={'Введите описание'}
                    formik={formik}
                    name={'description'}
                    description={'Причина списания средств'}
                />
            </FormRow>

            <FormButtons
                formik={formik}
                onCancel={props.onCancel}
                submitLabel={'Списать'}
            />
        </Form>
    );
};
